// src/components/Sidebar/Sidebar.jsx  (หรือ path เดิมของคุณ)
import React, { useEffect, useState } from "react";
import {
  NavLink as NavLinkRRD,
  Link,
  matchPath,
  useLocation,
} from "react-router-dom";
import PropTypes from "prop-types";
import { useAuth } from "../../auth/AuthContext";
import {
  Collapse,
  NavbarBrand,
  Navbar,
  NavItem,
  NavLink,
  Nav,
  Container,
} from "reactstrap";
import SidebarFlyout from "./SidebarFlyout";
import logoImg from "assets/logo.png";

const Sidebar = (props) => {
  // ===== Auth / Role =====
  const { user } = useAuth();
  const role = user?.role?.toLowerCase() === "admin" ? "admin" : "user";
  const canSee = (item) =>
    !item.hidden && (!item.roles || (role && item.roles.includes(role)));

  // ===== Location =====
  const location = useLocation();

  // ✅ FULL sidebar (ไม่ย่อ)
  const isMini = false;

  // ใช้ค่าเดิมไว้ได้ ถึงแม้ full จะไม่ใช้ flyout
  const [hoverGroup, setHoverGroup] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const sidebarMiniWidth = 72;

  // ===== Active check (exact only) =====
  const isActiveExact = (path) =>
    !!matchPath({ path, end: true }, location.pathname);

  // ✅ เอา class sidebar-mini ออก ไม่บังคับย่อ
  useEffect(() => {
    document.body.classList.remove("sidebar-mini");
    localStorage.removeItem("sidebar_mini");
  }, []);

  // ปิด flyout เมื่อเปลี่ยนหน้า (เผื่อยังใช้อยู่)
  useEffect(() => {
    setHoverGroup(null);
  }, [location.pathname]);

  // ✅ ไม่โชว์ sidebar เฉพาะหน้า index (ทำหลัง hooks)
  const HIDE_SIDEBAR_PATHS = [""];
  const shouldHideSidebar = HIDE_SIDEBAR_PATHS.includes(location.pathname);
  if (shouldHideSidebar) return null;

  // ===== Helper =====
  const renderIcon = (icon) => {
    if (!icon) return null;
    if (typeof icon === "string") return <i className={icon} />;
    if (React.isValidElement(icon))
      return React.cloneElement(icon, { className: "nav-icon" });
    if (typeof icon === "function") {
      const C = icon;
      return <C className="nav-icon" />;
    }
    return null;
  };

  // ===== Open flyout (จะทำงานเฉพาะ mini) =====
  const handleGroupToggle = (key, el) => {
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setAnchorRect(rect);
    setHoverGroup((curr) => (curr === key ? null : key));
  };

  // ===== Create links =====
  const createLinks = () => {
    const visible = (items) => items.filter(canSee);

    return visible(props.routes).map((prop, key) => {
      if (prop.children?.length) {
        const children = visible(prop.children);
        if (!children.length) return null;

        const anyChildActive = children.some((c) => isActiveExact(c.path));

        // ✅ FULL: แสดงหัวข้อ + รายการลูกแบบปกติ (ไม่ใช้ flyout)
        if (!isMini) {
          return (
            <React.Fragment key={`grp-${key}`}>
              <NavItem className="mt-2 mb-0 px-3">
                <div
                  className="text-uppercase text-muted small"
                  style={{ fontWeight: 700, letterSpacing: 0.6 }}
                >
                  {prop.name}
                </div>
              </NavItem>

              {children.map((c, idx) => (
                <NavItem key={`grp-${key}-${idx}`}>
                  <NavLink
                    to={c.path}
                    tag={NavLinkRRD}
                    className={`nav-link nav-leaf d-flex align-items-center ${
                      isActiveExact(c.path) ? "active" : ""
                    }`}
                    title={c.name}
                    onClick={() => setHoverGroup(null)}
                  >
                    {renderIcon(c.icon)}
                    <span className="nav-link-text ml-2">{c.name}</span>
                  </NavLink>
                </NavItem>
              ))}
            </React.Fragment>
          );
        }

        // ✅ MINI: (เผื่อกลับมาใช้) icon-only + flyout
        return (
          <NavItem key={`grp-mini-${key}`} className="mb-0">
            <a
              href="#!"
              onClick={(e) => {
                e.preventDefault();
                handleGroupToggle(key, e.currentTarget);
              }}
              className={`nav-link d-flex align-items-center ${
                anyChildActive ? "active" : ""
              }`}
              title={prop.name}
            >
              {renderIcon(prop.icon)}
            </a>

            {hoverGroup === key && (
              <SidebarFlyout
                items={children.map((c) => ({ name: c.name, path: c.path }))}
                anchorRect={anchorRect}
                sidebarWidth={sidebarMiniWidth}
                onClose={() => setHoverGroup(null)}
              />
            )}
          </NavItem>
        );
      }

      // ✅ leaf
      return (
        <NavItem key={key}>
          <NavLink
            to={prop.path}
            tag={NavLinkRRD}
            className={`nav-link nav-leaf d-flex align-items-center ${
              isActiveExact(prop.path) ? "active" : ""
            }`}
            title={prop.name}
            onClick={() => setHoverGroup(null)}
          >
            {renderIcon(prop.icon)}
            {!isMini && <span className="nav-link-text ml-2">{prop.name}</span>}
          </NavLink>
        </NavItem>
      );
    });
  };

  // ===== Logo =====
  const { logo } = props;
  const navbarBrandProps = logo?.innerLink
    ? { to: logo.innerLink, tag: Link }
    : logo?.outterLink
      ? { href: logo.outterLink, target: "_blank" }
      : {};

  return (
    <Navbar
      className="navbar-vertical fixed-left navbar-light bg-white" // ✅ ตัด sidebar-mini ออก
      expand="md"
      id="sidenav-main"
    >
      <Container fluid>
        {logo && (
          <NavbarBrand className="pt-3 pb-2 mx-auto d-flex justify-content-center">
            <img
              alt="logo"
              src={logoImg}
              style={{
                height: 42,
                width: "auto",
                display: "block",
                margin: "0 auto",
              }}
            />
          </NavbarBrand>
        )}

        <Collapse navbar isOpen>
          <Nav navbar className="mt-2">
            {createLinks()}
          </Nav>
        </Collapse>
      </Container>
    </Navbar>
  );
};

Sidebar.defaultProps = { routes: [{}] };
Sidebar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object),
  logo: PropTypes.shape({
    innerLink: PropTypes.string,
    outterLink: PropTypes.string,
    imgSrc: PropTypes.string.isRequired,
    imgAlt: PropTypes.string.isRequired,
  }),
};

export default Sidebar;
